// src/components/FranchiseManager.js
// Super Admin component — manage franchise chains and trigger menu syncs
// Used in SuperAdminPanel under the "⛓ Chains" tab

import React, { useState, useEffect, useCallback } from 'react';
import ConfirmModal from './ConfirmModal';
import {
  getFranchiseChain, getFranchiseLogs,
  syncFranchiseAll, syncFranchiseOne, setFranchiseHub,
} from '../services/api';
import api from '../services/api';

const SYNC_MODES = [
  { value: 'MERGE',        label: 'Merge',        desc: 'Add/update hub items; keep branch-only items' },
  { value: 'FULL_REPLACE', label: 'Full Replace',  desc: 'Replace all hub-synced items; keep manual items' },
];

function statusBadge(lastSync) {
  if (!lastSync) return { label: 'Never synced', color: 'var(--red)',    bg: '#3d1a1a' };
  const age = Date.now() - new Date(lastSync).getTime();
  if (age < 3_600_000) return { label: 'In sync',     color: 'var(--green)', bg: '#1a3d2a' };
  return              { label: 'Stale',          color: 'var(--accent)', bg: '#3d2e10' };
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function FranchiseManager() {
  const [confirm, setConfirm] = useState(null); // { message, onConfirm } for ConfirmModal
  const [vendors,      setVendors]      = useState([]);
  const [chains,       setChains]       = useState([]);   // hubs that have branches
  const [unassigned,   setUnassigned]   = useState([]);   // standalone vendors
  const [selectedHub,  setSelectedHub]  = useState(null);
  const [chainDetail,  setChainDetail]  = useState(null);
  const [logs,         setLogs]         = useState([]);
  const [assignForm,   setAssignForm]   = useState(null); // open assign modal
  const [syncing,      setSyncing]      = useState({});   // { [branchId]: bool }
  const [syncingAll,   setSyncingAll]   = useState(false);
  const [msg,          setMsg]          = useState('');
  const [loading,      setLoading]      = useState(false);

  const showMsg = (t) => { setMsg(t); setTimeout(() => setMsg(''), 4000); };

  // ── Load all vendors ───────────────────────────────────────
  const loadVendors = useCallback(async () => {
    const r = await api.get('/vendors').catch(() => ({ data: [] }));
    const all = r.data || [];
    setVendors(all);

    // Build derived lists
    const hubs = all.filter(v => !v.parentVendorId && all.some(b => b.parentVendorId === v.id));
    setChains(hubs);
    const unassigned_ = all.filter(v => !v.parentVendorId && !all.some(b => b.parentVendorId === v.id));
    setUnassigned(unassigned_);
  }, []);

  useEffect(() => { loadVendors(); }, [loadVendors]);

  // ── Load chain detail when hub is selected ─────────────────
  useEffect(() => {
    if (!selectedHub) { setChainDetail(null); setLogs([]); return; }
    getFranchiseChain(selectedHub.id)
      .then(r => setChainDetail(r.data))
      .catch(() => setChainDetail(null));
    getFranchiseLogs(selectedHub.id)
      .then(r => setLogs(r.data || []))
      .catch(() => setLogs([]));
  }, [selectedHub]);

  // ── Sync all branches for selected hub ─────────────────────
  const handleSyncAll = async () => {
    if (!selectedHub) return;
    setSyncingAll(true);
    try {
      const r = await syncFranchiseAll(selectedHub.id);
      showMsg(`✅ Synced ${r.data.synced} items across ${r.data.total} branches`);
      getFranchiseChain(selectedHub.id).then(r => setChainDetail(r.data)).catch(() => {});
      getFranchiseLogs(selectedHub.id).then(r => setLogs(r.data || [])).catch(() => {});
    } catch (e) {
      showMsg('❌ ' + (e.response?.data?.error || 'Sync failed'));
    } finally { setSyncingAll(false); }
  };

  // ── Sync one branch ───────────────────────────────────────
  const handleSyncOne = async (branchId) => {
    setSyncing(p => ({ ...p, [branchId]: true }));
    try {
      const r = await syncFranchiseOne(selectedHub.id, branchId);
      showMsg(`✅ Synced ${r.data.synced} items to branch`);
      getFranchiseChain(selectedHub.id).then(r => setChainDetail(r.data)).catch(() => {});
      getFranchiseLogs(selectedHub.id).then(r => setLogs(r.data || [])).catch(() => {});
    } catch (e) {
      showMsg('❌ ' + (e.response?.data?.error || 'Sync failed'));
    } finally { setSyncing(p => ({ ...p, [branchId]: false })); }
  };

  // ── Update branch settings ─────────────────────────────────
  const handleBranchUpdate = async (branchId, updates) => {
    setLoading(true);
    try {
      await setFranchiseHub({ branchId, hubId: selectedHub.id, ...updates });
      showMsg('✅ Settings saved');
      getFranchiseChain(selectedHub.id).then(r => setChainDetail(r.data)).catch(() => {});
    } catch (e) {
      showMsg('❌ ' + (e.response?.data?.error || 'Save failed'));
    } finally { setLoading(false); }
  };

  // ── Detach a branch ───────────────────────────────────────
  const handleDetach = async (branchId) => {
    setLoading(true);
    try {
      await setFranchiseHub({ branchId, hubId: null });
      showMsg('✅ Branch detached');
      await loadVendors();
      getFranchiseChain(selectedHub.id).then(r => setChainDetail(r.data)).catch(() => {});
    } catch (e) {
      showMsg('❌ ' + (e.response?.data?.error || 'Detach failed'));
    } finally { setLoading(false); }
  };

  // ── Assign branch modal save ──────────────────────────────
  const handleAssign = async () => {
    if (!assignForm?.branchId || !assignForm?.hubId) {
      showMsg('❌ Select both a hub and a branch'); return;
    }
    if (assignForm.branchId === assignForm.hubId) {
      showMsg('❌ Hub and branch cannot be the same vendor'); return;
    }
    setLoading(true);
    try {
      await setFranchiseHub({
        branchId:              assignForm.branchId,
        hubId:                 assignForm.hubId,
        franchiseSyncMode:     assignForm.syncMode || 'MERGE',
        franchisePriceOverride: assignForm.priceOverride || false,
      });
      showMsg('✅ Chain relationship created');
      setAssignForm(null);
      await loadVendors();
      // If newly created hub, select it
      const hub = vendors.find(v => v.id === assignForm.hubId);
      if (hub) setSelectedHub(hub);
    } catch (e) {
      showMsg('❌ ' + (e.response?.data?.error || 'Assign failed'));
    } finally { setLoading(false); }
  };

  return (
    <div>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      {/* Global message */}
      {msg && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: msg.startsWith('✅') ? '#1a3d2a' : '#3d1a1a',
          color:      msg.startsWith('✅') ? 'var(--green)' : 'var(--red)',
          border: `1px solid ${msg.startsWith('✅') ? 'var(--green)' : 'var(--red)'}`,
        }}>{msg}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 20, alignItems: 'start' }}>

        {/* ── Left: Chain List ── */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>⛓ Chains ({chains.length})</div>
            <button className="btn btn-primary btn-sm" onClick={() => setAssignForm({ syncMode: 'MERGE', priceOverride: false })}>
              + Create Chain
            </button>
          </div>

          {/* Chain list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
            {chains.map(hub => {
              const branchCount = vendors.filter(v => v.parentVendorId === hub.id).length;
              const isSelected  = selectedHub?.id === hub.id;
              return (
                <div key={hub.id} onClick={() => setSelectedHub(hub)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                    borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                    background: isSelected ? '#3d2e10' : 'var(--bg2)',
                  }}>
                  <span style={{ fontSize: 20 }}>{hub.emoji || '🍽'}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: isSelected ? 'var(--accent)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hub.name}
                    </div>
                  </div>
                  <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10, background: 'var(--bg3)', color: 'var(--accent)', fontWeight: 700, flexShrink: 0 }}>
                    {branchCount}
                  </span>
                </div>
              );
            })}
            {chains.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '20px 0' }}>
                No chains yet. Click "+ Create Chain" to get started.
              </div>
            )}
          </div>

          {/* Unassigned vendors */}
          {unassigned.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>
                Unassigned ({unassigned.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {unassigned.map(v => (
                  <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 6, background: 'var(--bg2)', border: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 16 }}>{v.emoji || '🍽'}</span>
                    <span style={{ fontSize: 12, color: 'var(--text2)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── Right: Chain Detail ── */}
        {selectedHub ? (
          <div>
            {/* Hub header */}
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <span style={{ fontSize: 28 }}>{selectedHub.emoji || '🍽'}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{selectedHub.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text2)' }}>
                    Hub · {chainDetail?.hub?.menuItemCount ?? '…'} active menu items
                  </div>
                </div>
                <button
                  className="btn btn-primary"
                  disabled={syncingAll}
                  onClick={handleSyncAll}>
                  {syncingAll ? '⏳ Syncing...' : '🔄 Push to All Branches'}
                </button>
              </div>
            </div>

            {/* Branches table */}
            <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: 16 }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 13 }}>
                Branches ({chainDetail?.branches?.length ?? 0})
              </div>
              {!chainDetail?.branches?.length ? (
                <div style={{ textAlign: 'center', padding: 32, color: 'var(--text3)', fontSize: 13 }}>No branches yet</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Branch', 'Sync Mode', 'Price Override', 'Last Sync', 'Items', 'Status', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {chainDetail.branches.map((b, i) => {
                      const badge = statusBadge(b.franchiseLastSync);
                      return (
                        <tr key={b.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ fontWeight: 600 }}>{b.emoji || '🍽'} {b.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{b.email}</div>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <select
                              value={b.franchiseSyncMode || 'MERGE'}
                              style={{ fontSize: 12, padding: '4px 8px' }}
                              onChange={e => handleBranchUpdate(b.id, { franchiseSyncMode: e.target.value })}>
                              {SYNC_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                            </select>
                          </td>
                          <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                            <input type="checkbox"
                              checked={!!b.franchisePriceOverride}
                              onChange={e => handleBranchUpdate(b.id, { franchisePriceOverride: e.target.checked })} />
                          </td>
                          <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                            {fmtDate(b.franchiseLastSync)}
                          </td>
                          <td style={{ padding: '10px 14px', color: 'var(--accent)', fontWeight: 600 }}>
                            {b.menuItemCount}
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: badge.bg, color: badge.color }}>
                              {badge.label}
                            </span>
                          </td>
                          <td style={{ padding: '10px 14px' }}>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button
                                className="btn btn-ghost btn-sm"
                                disabled={syncing[b.id]}
                                onClick={() => handleSyncOne(b.id)}
                                style={{ fontSize: 11 }}>
                                {syncing[b.id] ? '⏳' : '🔄'} Sync
                              </button>
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setConfirm({ message: 'Detach this branch? It will become a standalone vendor.', confirmLabel: 'Detach', onConfirm: () => handleDetach(b.id) })}
                                style={{ fontSize: 11, color: 'var(--red)' }}>
                                Detach
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Sync Logs */}
            {logs.length > 0 && (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', fontWeight: 700, fontSize: 13 }}>
                  Recent Sync Logs
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)' }}>
                      {['Date', 'Branch', 'Mode', 'Items', 'Duration', 'Status'].map(h => (
                        <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {logs.slice(0, 10).map((l, i) => {
                      const branch = vendors.find(v => v.id === l.targetId);
                      return (
                        <tr key={l.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg3)' }}>
                          <td style={{ padding: '8px 14px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>{fmtDate(l.createdAt)}</td>
                          <td style={{ padding: '8px 14px' }}>{branch?.name || l.targetId.slice(-8)}</td>
                          <td style={{ padding: '8px 14px', color: 'var(--text2)' }}>{l.syncMode}</td>
                          <td style={{ padding: '8px 14px', color: 'var(--accent)', fontWeight: 600 }}>{l.itemsSynced}</td>
                          <td style={{ padding: '8px 14px', color: 'var(--text3)' }}>{l.duration ? `${l.duration}ms` : '—'}</td>
                          <td style={{ padding: '8px 14px' }}>
                            <span style={{ padding: '2px 7px', borderRadius: 5, fontSize: 11, fontWeight: 600,
                              background: l.status === 'success' ? '#1a3d2a' : '#3d1a1a',
                              color:      l.status === 'success' ? 'var(--green)' : 'var(--red)' }}>
                              {l.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, color: 'var(--text3)', fontSize: 13 }}>
            ← Select a chain to view details
          </div>
        )}
      </div>

      {/* ── Assign Branch Modal ── */}
      {assignForm !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'var(--bg2)', borderRadius: 16, padding: 28, width: '100%', maxWidth: 460, border: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 20 }}>⛓ Create Chain Relationship</div>

            <div style={{ marginBottom: 14 }}>
              <div className="section-label" style={{ marginBottom: 6 }}>Hub (master menu)</div>
              <select value={assignForm.hubId || ''} onChange={e => setAssignForm(p => ({ ...p, hubId: e.target.value }))}>
                <option value="">— Select hub vendor —</option>
                {vendors.filter(v => !v.parentVendorId).map(v => (
                  <option key={v.id} value={v.id}>{v.emoji} {v.name}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div className="section-label" style={{ marginBottom: 6 }}>Branch (receives menu)</div>
              <select value={assignForm.branchId || ''} onChange={e => setAssignForm(p => ({ ...p, branchId: e.target.value }))}>
                <option value="">— Select branch vendor —</option>
                {vendors.filter(v => !v.parentVendorId && v.id !== assignForm.hubId).map(v => (
                  <option key={v.id} value={v.id}>{v.emoji} {v.name}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div className="section-label" style={{ marginBottom: 6 }}>Sync Mode</div>
              <select value={assignForm.syncMode || 'MERGE'} onChange={e => setAssignForm(p => ({ ...p, syncMode: e.target.value }))}>
                {SYNC_MODES.map(m => <option key={m.value} value={m.value}>{m.label} — {m.desc}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <input type="checkbox" id="priceOverride"
                checked={!!assignForm.priceOverride}
                onChange={e => setAssignForm(p => ({ ...p, priceOverride: e.target.checked }))} />
              <label htmlFor="priceOverride" style={{ fontSize: 13, cursor: 'pointer' }}>
                Keep branch's local prices (do not overwrite from hub)
              </label>
            </div>

            {msg && (
              <div style={{ padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 12,
                background: msg.startsWith('✅') ? '#1a3d2a' : '#3d1a1a',
                color:      msg.startsWith('✅') ? 'var(--green)' : 'var(--red)' }}>
                {msg}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-primary" style={{ flex: 2 }} disabled={loading} onClick={handleAssign}>
                {loading ? 'Saving...' : 'Create Chain'}
              </button>
              <button className="btn btn-ghost" style={{ flex: 1 }} onClick={() => setAssignForm(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
